import { useExperimentsFromDualInstances } from '@linktr.ee/feature-flags-mfe/DualStatsigInstances';
import { useEffect, useRef, useState } from 'react';
import { normaliseRoute } from '@shared/utils/normaliseRoute';
import { ScreenNames } from './screenMapping';

export const PAID_MEDIA_SIGNUP_COPY_EXPERIMENT = 'seo--paid-media-signup-copy';

const CONTROL_VARIANT = 'control';
const TREATMENT_VARIANT = 'test';
const PAID_MEDIA_ENTRY_POINT = 'PAID_MEDIA';
const REGISTER_ROUTE = '/register';
const ENTRY_POINT_PARAM = 'freeTrialEntryPoint';

export type SignupEntryPointLocation = Pick<Location, 'hash' | 'search'>;

// Eligibility is read once when the flow mounts, so this answers "did this
// session enter via paid media", not "is the current route paid media". Paid
// campaigns only ever deep-link to `#/register`.
export const isPaidMediaSignupEntryPoint = ({
  hash,
  search,
}: SignupEntryPointLocation): boolean => {
  if (normaliseRoute(hash) !== REGISTER_ROUTE) {
    return false;
  }

  const hashSearchParams = new URLSearchParams(hash.split('?')[1] ?? '');
  const searchParams = new URLSearchParams(search);
  const entryPoint =
    searchParams.get(ENTRY_POINT_PARAM) ??
    hashSearchParams.get(ENTRY_POINT_PARAM);

  return entryPoint === PAID_MEDIA_ENTRY_POINT;
};

export const usePaidMediaSignupVariant = (
  currentScreenName: string | undefined
): boolean => {
  const {
    getExperimentFromWarehouseNative,
    getExperimentFromWarehouseNativeWithoutExposure,
    initialised,
  } = useExperimentsFromDualInstances();
  const [isPaidMediaSignup] = useState(() =>
    isPaidMediaSignupEntryPoint(window.location)
  );
  // Only eligible sessions actually looking at the signup screen belong in the
  // experiment, so both the rendered variant and the exposure hang off this.
  const isPaidMediaSignupScreen =
    isPaidMediaSignup &&
    initialised &&
    currentScreenName === ScreenNames.SIGNUP_PAGE;
  const hasLoggedExposureRef = useRef(false);

  useEffect(() => {
    if (!isPaidMediaSignupScreen || hasLoggedExposureRef.current) {
      return;
    }

    hasLoggedExposureRef.current = true;
    // Note the inverted naming against the gate API: for experiments it is the
    // bare call that logs the exposure.
    getExperimentFromWarehouseNative(PAID_MEDIA_SIGNUP_COPY_EXPERIMENT);
  }, [getExperimentFromWarehouseNative, isPaidMediaSignupScreen]);

  // Read without exposure so rendering stays pure; the effect above owns the
  // single exposure log. Deliberately unmemoised: the read is a cheap local
  // lookup, and caching it on the provider callback's identity would pin the
  // rendered arm to the first evaluation.
  return (
    isPaidMediaSignupScreen &&
    getExperimentFromWarehouseNativeWithoutExposure(
      PAID_MEDIA_SIGNUP_COPY_EXPERIMENT,
      { variant: CONTROL_VARIANT }
    )?.variant === TREATMENT_VARIANT
  );
};
